/**
 * Utility functions to determine the completion status of Pro Calculator sections
 */

/**
 * Determines if the Reference section is complete
 * @param {Object} reference - The reference cookie data
 * @returns {string} - 'completed', 'incomplete', or 'neutral'
 */
export const getReferenceStatus = (reference, showAdvanced = false) => {
  if (!reference) {
    return 'neutral';
  }

  const isValidField = (field) => {
    const isValid = field !== null && field !== undefined && String(field).trim() !== '';
    return isValid;
  };

  if (!showAdvanced || reference.useDefaultDensity !== false) {
    // Standard mode OR Advanced with default density selected — global default drives density, always ready
    return 'completed';
  }

  // Advanced mode, custom density: user must supply enough data to produce a valid icing density
  const { shapeType, width, length, diameter, manualArea, weightNoIcing, weight } = reference;
  const shape = shapeType || 'rectangle';

  let area = 0;
  if (shape === 'rectangle') {
    const w = parseFloat(width); const l = parseFloat(length);
    if (!isNaN(w) && !isNaN(l)) area = w * l;
  } else if (shape === 'square') {
    const s = parseFloat(width);
    if (!isNaN(s)) area = s * s;
  } else if (shape === 'circle') {
    const d = parseFloat(diameter);
    if (!isNaN(d) && d > 0) area = Math.PI * (d / 2) * (d / 2);
  } else if (shape === 'manual') {
    const a = parseFloat(manualArea);
    if (!isNaN(a)) area = a;
  }

  const wni = parseFloat(weightNoIcing);
  const w = parseFloat(weight);
  const hasValidDensity = area > 0 && !isNaN(wni) && !isNaN(w) && w > wni;

  // Check if anything has been touched at all
  const anyFilled = isValidField(width) || isValidField(length) || isValidField(diameter)
    || isValidField(manualArea) || isValidField(weightNoIcing) || isValidField(weight);

  if (hasValidDensity) return 'completed';
  if (anyFilled) return 'incomplete';
  return 'incomplete';
};

/**
 * Determines if the Cookie Names section is complete
 * @param {Array} cookieRows - The cookie rows data
 * @returns {string} - 'completed', 'incomplete', or 'neutral'
 */
export const getCookieNamesStatus = (cookieRows) => {
  if (!cookieRows || cookieRows.length === 0) {
    return 'neutral'; // Or 'incomplete' if adding the first row is expected
  }

  const allNamed = cookieRows.every(row => row.cookieName && String(row.cookieName).trim() !== '');
  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const namedRowsCount = cookieRows.filter(row => row.cookieName && String(row.cookieName).trim() !== '').length;

  if (allNamed) {
    return 'completed';
  } else {
    return 'incomplete';
  }
};

const isValidPositiveNumber = (value) => {
  const num = parseFloat(value);
  return !isNaN(num) && num > 0;
};

/**
 * Determines if the Dimensions section is complete
 * @param {Array} dimensionsRows - The dimensions data
 * @returns {string} - 'completed' or 'incomplete'
 */
export const getDimensionsStatus = (dimensionsRows) => {
  if (!dimensionsRows || dimensionsRows.length === 0) {
    return 'incomplete'; 
  }

  for (const row of dimensionsRows) {
    const { shapeType, width, length, diameter, area } = row;

    if (!shapeType || shapeType === '') {
      return 'incomplete'; 
    }

    switch (shapeType) {
      case 'square':
        if (!isValidPositiveNumber(width)) return 'incomplete';
        break;
      case 'rectangle':
        if (!isValidPositiveNumber(width) || !isValidPositiveNumber(length)) return 'incomplete';
        break;
      case 'circle':
        if (!isValidPositiveNumber(diameter)) return 'incomplete';
        break;
      case 'triangle': 
        if (!isValidPositiveNumber(width) || !isValidPositiveNumber(length)) return 'incomplete';
        break;
      case 'custom':
        if (!isValidPositiveNumber(area)) return 'incomplete';
        break;
      case 'override':
      case 'photo':
        if (!isValidPositiveNumber(row.overrideArea)) return 'incomplete';
        break;
      default:
        return 'incomplete'; 
    }
  }

  return 'completed';
};

/**
 * Determines if the Sets section is complete
 * @param {Boolean} useSets - Whether sets are being used
 * @param {Array} setRows - The sets data
 * @returns {string} - 'completed', 'incomplete', or 'neutral'
 */
export const getSetsStatus = (useSets, setRows) => {
  if (!useSets) {
    return 'completed';
  }

  if (setRows.length === 0) {
    return 'incomplete';
  }
  
  const hasSelectedCookie = setRows.some(row => row.cookieId && row.cookieId !== '');
  
  return hasSelectedCookie ? 'completed' : 'incomplete';
};

/**
 * Determines if the Colors section is complete
 * @param {Object} data - The colors data including colorRows and colorNames
 * @returns {string} - 'completed', 'incomplete', or 'neutral'
 */
export const getColorsStatus = (data) => {
  if (!data) return 'completed'; 

  // eslint-disable-next-line no-unused-vars -- 3.4.2026
  const { colorRows, colorNames, cookieRows } = data;

  if (!colorRows || colorRows.length === 0 || !colorNames) {
    return 'completed';
  }

  for (const row of colorRows) {
    const percentages = row.percentages || [];

    // If there are 3+ colors, percentages must add up to 100
    if (colorNames.length >= 3) {
      if (percentages.length !== colorNames.length) return 'incomplete';
      const sum = percentages.reduce((acc, p) => acc + (Number(p) || 0), 0);
      if (Math.abs(sum - 100) > 0.01) return 'incomplete';
    } else {
      // For 1–2 colors, flag if ALL percentages are zero (nothing allocated)
      const sum = percentages.reduce((acc, p) => acc + (Number(p) || 0), 0);
      if (sum === 0) return 'incomplete';
    }
  }

  return 'completed';
};

/**
 * Determines if the Orders section is complete
 * @param {Array} orderRows - The orders data
 * @returns {string} - 'completed', 'incomplete', or 'neutral'
 */
export const getOrdersStatus = (orderRows) => {
  if (!orderRows || orderRows.length === 0) return 'neutral';

  const relevantRows = orderRows.filter(row => row.cookieId);
  if (relevantRows.length === 0) return 'neutral';

  // Red if every cookie is still at zero (nothing to show on Results yet);
  // yellow if some are filled in and some aren't (still usable, just a heads-up);
  // green only once every cookie has a count.
  const withCount = relevantRows.filter(row => Number(row.count) > 0).length;
  if (withCount === 0) return 'incomplete';
  if (withCount === relevantRows.length) return 'completed';
  return 'warning';
};

/**
 * Determines if the Results section is complete
 * @param {Object} statuses - The statuses of other sections
 * @returns {string} - 'completed', 'incomplete', or 'neutral'
 */
export const getResultsStatus = (statuses) => {
  const requiredSections = ['reference', 'cookieNames', 'dimensions'];

  const allRequiredComplete = requiredSections.every(section =>
    statuses[section] === 'completed'
  );

  const anyRequiredIncomplete = requiredSections.some(section =>
    statuses[section] === 'incomplete'
  );

  // How Many (or Sets, when that mode is on) feeds Results too — if it's red,
  // Results isn't wrong, it just doesn't have real numbers to show yet.
  const quantityIncomplete = statuses.useSets
    ? statuses.sets === 'incomplete'
    : statuses.orders === 'incomplete';

  if (allRequiredComplete && !quantityIncomplete) {
    return 'completed';
  } else if (anyRequiredIncomplete || quantityIncomplete) {
    // Not wrong, just not ready yet — a step feeding this one isn't done.
    return 'warning';
  }

  return 'neutral';
};

/**
 * Calculates the completion status for all Pro Calculator sections
 * @param {Object} calculatorState - The current state of the Pro Calculator
 * @returns {Object} - An object with the status of each section
 */
export const calculateSectionCompletionStatus = (calculatorState) => {
  if (!calculatorState || Object.keys(calculatorState).length === 0) {
    return {
      reference: 'neutral',
      cookieNames: 'neutral',
      dimensions: 'neutral',
      sets: 'neutral',
      colors: 'neutral',
      orders: 'neutral',
      results: 'neutral',
      all: 'neutral'
    };
  }
  
  const reference = calculatorState.reference || {};
  const cookieRows = calculatorState.cookieRows || [];
  const dimensionsRows = calculatorState.dimensionsRows || [];
  const setRows = calculatorState.setRows || [];
  const orderRows = calculatorState.orderRows || [];
  const useSets = calculatorState.useSets || false;
  
  const colorData = {
    colorRows: calculatorState.colorRows || [],
    colorNames: calculatorState.colorNames || []
  };
  
  const showAdvanced = calculatorState.showAdvanced || false;
  const referenceStatus = getReferenceStatus(reference, showAdvanced);
  const cookieNamesStatus = getCookieNamesStatus(cookieRows);
  const setsStatus = useSets ? getSetsStatus(useSets, setRows) : 'completed'; // Fixed: Pass useSets and setRows separately
  const dimensionsStatus = getDimensionsStatus(dimensionsRows);
  const colorsStatus = getColorsStatus(colorData);
  const ordersStatus = getOrdersStatus(orderRows);
  const resultsStatus = getResultsStatus({
    reference: referenceStatus,
    cookieNames: cookieNamesStatus,
    dimensions: dimensionsStatus,
    orders: ordersStatus,
    sets: setsStatus,
    useSets
  });

  const status = {
    reference: referenceStatus,
    cookieNames: cookieNamesStatus,
    dimensions: dimensionsStatus,
    sets: setsStatus,
    colors: colorsStatus,
    orders: ordersStatus,
    results: resultsStatus
  };

  return status;
};
